import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";
import { findProduct } from "@/data/productsData";

const RELEVANT_SLUGS = [
  "solvent-hp-compatible-cartridge",
  "water-based-hp-black-cartridge",
  "porous-ink",
];

const IndustryFood = () => {
  const path = "/industries/food";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Food Manufacturing",
    description:
      "Selected coding cartridges and printing inks may be suitable for food manufacturing applications subject to product-specific technical evaluation. Pharmaceutical manufacturing remains our primary focus.",
    url: canonical,
  };

  const relevant = RELEVANT_SLUGS.map(findProduct).filter(Boolean);

  return (
    <>
      <SEO
        rawTitle={`Food Manufacturing Coding Applications | ${siteConfig.COMPANY_NAME}`}
        description="Selected coding cartridges and printing inks may be suitable for food manufacturing as a secondary application, subject to product-specific technical evaluation."
        path={path}
        extraSchema={schema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Industries" },
            { label: "Food Manufacturing" },
          ]}
        />
      </div>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-10 lg:pb-14"
        data-testid="industry-food-hero"
      >
        <p className="eyebrow" style={{ color: "var(--slate)" }}>
          Secondary Industry
        </p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[42px] lg:leading-[1.05] font-semibold text-[var(--navy)] max-w-3xl"
          data-testid="industry-food-h1"
        >
          Food Manufacturing
        </h1>
        <div className="mt-6 space-y-4 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          <p>
            Pharmaceutical manufacturing is our primary focus. The information
            on this page refers to a secondary application area only.
          </p>
          <p>
            Certain HP-compatible thermal inkjet coding cartridges and selected
            printing inks in our portfolio are also used for batch and expiry
            coding on food-packaging substrates. Any use in a food-manufacturing
            application is subject to product-specific application testing under
            the customer&apos;s actual production conditions.
          </p>
          <p>
            Suitability for food-industry applications depends on the specific
            product, substrate and regulatory environment in your market.
            Please contact our team for a technical review before considering
            supply.
          </p>
        </div>
      </section>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-10 lg:py-14 border-t border-[var(--border)]"
        data-testid="industry-food-relevant"
      >
        <h2 className="font-heading text-xl lg:text-2xl font-semibold text-[var(--navy)]">
          Products That May Be Relevant
        </h2>
        <p className="mt-3 text-[14px] text-[var(--slate)] max-w-2xl">
          The following products are the most commonly discussed for food-industry
          coding enquiries. Suitability is confirmed through application review.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {relevant.map((p) => (
            <Link
              key={p.slug}
              to={`/products/${p.slug}`}
              className="border border-[var(--border)] rounded-[4px] p-6 bg-white hover:bg-[var(--bg-alt)] transition-colors flex flex-col"
              data-testid={`industry-food-product-${p.slug}`}
            >
              <p className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[var(--pharma-blue)]">
                {p.categoryLabel}
              </p>
              <p className="font-heading mt-2 text-[17px] font-semibold text-[var(--navy)]">
                {p.name}
              </p>
              <p className="mt-3 text-[14px] leading-relaxed text-[var(--slate)] flex-1">
                {p.shortDescription.split(". ")[0]}.
              </p>
              <span className="arrow-link mt-4">
                View Product <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16"
        data-testid="industry-food-cta"
      >
        <div className="border border-[var(--border)] rounded-[4px] p-8 flex flex-col md:flex-row items-start md:items-center gap-5">
          <div className="flex-1">
            <h2 className="font-heading text-lg lg:text-xl font-semibold text-[var(--navy)]">
              Discuss a food-industry application
            </h2>
            <p className="mt-2 text-[14.5px] text-[var(--charcoal)] max-w-2xl">
              Share the machine, substrate and print requirement so our team
              can review whether a product from our portfolio is a suitable
              starting point.
            </p>
          </div>
          <Link
            to="/contact?type=technical&industry=food"
            className="btn-primary text-sm"
            data-testid="industry-food-cta-btn"
          >
            Discuss Food Manufacturing Application
          </Link>
        </div>
      </section>
    </>
  );
};

export default IndustryFood;
